import type { ReactNode } from "react";
import { App, Typography, message, notification } from "antd";

const HEADER = "x-request-id";
const RECENT_MS = 5000;

let lastFailed: { id: string; at: number } | null = null;

export function recordFailedRequestId(id: string | null | undefined) {
  if (id) lastFailed = { id, at: Date.now() };
}

export function getRecentFailedRequestId(now = Date.now()): string | undefined {
  if (!lastFailed || now - lastFailed.at > RECENT_MS) return undefined;
  return lastFailed.id;
}

export function resetRequestIdState() {
  lastFailed = null;
}

export const FETCH_PATCHED = Symbol.for("sfc.requestId.fetch");

export function wrapFetch(original: typeof fetch): typeof fetch {
  const wrapped = async (...args: Parameters<typeof fetch>) => {
    const res = await original(...args);
    if (!res.ok) recordFailedRequestId(res.headers.get(HEADER));
    return res;
  };
  (wrapped as any)[FETCH_PATCHED] = true;
  return wrapped as typeof fetch;
}

function installFetchHook() {
  const w = window as any;
  if (typeof w.fetch !== "function" || w.fetch[FETCH_PATCHED]) return;
  w.fetch = wrapFetch(w.fetch.bind(window));
}

export function RequestIdFooter({ id }: { id: string }) {
  return (
    <div
      data-testid="request-id-footer"
      style={{ marginTop: 4, fontSize: 12, userSelect: "text" }}
    >
      <Typography.Text type="secondary" copyable={{ text: id }} style={{ fontSize: 12 }}>
        request-id: {id}
      </Typography.Text>
    </div>
  );
}

function withFooter(content: ReactNode, id: string): ReactNode {
  return (
    <span style={{ display: "inline-block", textAlign: "left" }}>
      <div>{content}</div>
      <RequestIdFooter id={id} />
    </span>
  );
}

const isArgsProps = (v: unknown): v is { content: ReactNode } =>
  !!v && typeof v === "object" && "content" in (v as object) && !("$$typeof" in (v as object));

type MessageLike = { error: (...args: any[]) => any };
type NotificationLike = { error: (config: any) => any };

const PATCHED = Symbol.for("sfc.requestId.patched");
const HOOK_PATCHED = Symbol.for("sfc.requestId.hookPatched");

function patchMessageApi<T extends MessageLike>(api: T): T {
  if (!api || (api as any)[PATCHED]) return api;
  const original = api.error.bind(api);
  api.error = (content: any, ...rest: any[]) => {
    const id = getRecentFailedRequestId();
    if (!id) return original(content, ...rest);
    if (isArgsProps(content)) {
      return original({ ...content, content: withFooter(content.content, id) }, ...rest);
    }
    return original(withFooter(content, id), ...rest);
  };
  (api as any)[PATCHED] = true;
  return api;
}

function patchNotificationApi<T extends NotificationLike>(api: T): T {
  if (!api || (api as any)[PATCHED]) return api;
  const original = api.error.bind(api);
  api.error = (config: any) => {
    const id = getRecentFailedRequestId();
    if (!id || !config || typeof config !== "object") return original(config);
    const footer = <RequestIdFooter id={id} />;
    const description =
      config.description != null && config.description !== "" ? (
        <>
          {config.description}
          {footer}
        </>
      ) : (
        footer
      );
    return original({ ...config, description });
  };
  (api as any)[PATCHED] = true;
  return api;
}

function installAntdHooks() {
  patchMessageApi(message as MessageLike);
  patchNotificationApi(notification as NotificationLike);

  const m = message as any;
  if (!m[HOOK_PATCHED]) {
    const useMessage = m.useMessage;
    m.useMessage = (...args: any[]) => {
      const [api, holder] = useMessage(...args);
      return [patchMessageApi(api), holder];
    };
    m[HOOK_PATCHED] = true;
  }

  const n = notification as any;
  if (!n[HOOK_PATCHED]) {
    const useNotification = n.useNotification;
    n.useNotification = (...args: any[]) => {
      const [api, holder] = useNotification(...args);
      return [patchNotificationApi(api), holder];
    };
    n[HOOK_PATCHED] = true;
  }

  const a = App as any;
  if (!a[HOOK_PATCHED]) {
    const useApp = a.useApp;
    a.useApp = () => {
      const ctx = useApp();
      patchMessageApi(ctx.message);
      patchNotificationApi(ctx.notification);
      return ctx;
    };
    a[HOOK_PATCHED] = true;
  }
}

export function installRequestIdReporting() {
  installFetchHook();
  installAntdHooks();
}

installRequestIdReporting();
